import React, { useMemo, useState } from "react";
import html2pdf from "html2pdf.js";
import { buildTCO } from "../../../utils/tcoEngine";
import {
  Box,
  Backdrop,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";

// Styles (UNCHANGED)
const stickyHeader = {
  position: "sticky",
  top: 0,
  fontSize: "14px",
  fontFamily: "GilroySemiBold",
};

const stickyLeft = {
  position: "sticky",
  left: 0,
  top: 0,
  background: "#fff",
  zIndex: 3,
};

const cellStyle = {
  fontSize: "14px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  fontFamily: "Helvetica, Arial, sans-serif",
  borderRight: "1px solid #b8caee",
  whiteSpace: "wrap",
};

const headerStyle = {
  fontSize: "15px",
  fontWeight: 700,
  background: "#f1f5f9",
  whiteSpace: "nowrap",
  fontFamily: "Helvetica, Arial, sans-serif",
  borderRight: "1px solid #b8caee",
  whiteSpace: "wrap",
};

const TCOComparison = ({ jsonData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const vendorColWidth = isMobile ? 120 : isTablet ? 140 : 160;
  const firstColWidth = isMobile ? 150 : isTablet ? 170 : 200;
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportFontScale = 1.25;

  const rows = useMemo(() => buildTCO(jsonData || []), [jsonData]);
  console.log(rows, "rows");
  const formatINR = (value) =>
    `₹ ${Number(value || 0).toLocaleString("en-IN")}`;

  // summary

  const vendorSummary = useMemo(() => {
    const map = {};

    rows.forEach((r) => {
      if (!map[r.vendor]) {
        map[r.vendor] = {
          vendor: r.vendor,
          total: 0,
          count: 0,
        };
      }

      map[r.vendor].total += r.total || 0;
      map[r.vendor].count += 1;
    });

    return Object.values(map).map((v) => ({
      ...v,
      avg: v.count ? v.total / v.count : 0, // ⭐ Normalized TCO per item
    }));
  }, [rows]);

  const ranked = useMemo(
    () => [...vendorSummary].sort((a, b) => a.avg - b.avg),
    [vendorSummary],
  );

  const lowest = ranked[0]?.avg ?? 0;
  const highest = ranked[ranked.length - 1]?.avg ?? 0;

  const average =
    ranked.length > 0
      ? ranked.reduce((sum, r) => sum + r.avg, 0) / ranked.length
      : 0;

  const savings = ranked.length > 1 ? highest - lowest : 0;

  console.log(ranked, "ranked");

  const handleDownloadPdf = () => {
    if (!rows.length) return;
    const element = document.getElementById("tco_compare_view");
    if (!element) return;
    setIsDownloading(true);
    setIsExporting(true);
    const options = {
      margin: [2, 5],
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: {
        unit: rows.length < 5 ? "" : "",
        format: rows.length < 5 ? "a4" : [element.offsetWidth, 595.28],
        orientation: "landscape",
        margins: {
          top: 5,
          bottom: 5,
          left: 5,
          right: 5,
        },
      },
      filename: "tco-comparison.pdf",
    };

    setTimeout(() => {
      html2pdf()
        .set(options)
        .from(element)
        .save()
        .then(() => {
          setIsDownloading(false);
          setIsExporting(false);
        })
        .catch(() => {
          setIsDownloading(false);
          setIsExporting(false);
        });
    }, 300);
  };

  // ------------------- VERTICAL STRUCTURE -------------------

  const verticalRows = useMemo(() => {
    if (!rows.length) return [];

    const fields = [
      { key: "category", label: "Category" },
      { key: "brand", label: "Brand" },
      { key: "itemName", label: "Item" },
      { key: "quantity", label: "Quantity" },
      { key: "unitPrice", label: "Unit Price", format: formatINR },

      { key: "base", label: "Basic Price", format: formatINR },
      { key: "gst", label: "GST", format: formatINR },

      { key: "logistics", label: "Logistics", format: formatINR },
      { key: "freight", label: "Freight", format: formatINR },
      { key: "installation", label: "Installation", format: formatINR },

      { key: "advancePayment", label: "Advance Payment", format: formatINR },
      { key: "creditDays", label: "Credit (Days)" },
      { key: "finance", label: "Finance Cost", format: formatINR },
      { key: "lcCost", label: "LC Charges", format: formatINR },

      { key: "support9x5", label: "9x5 Support", format: formatINR },
      { key: "support24x7", label: "24x7 Support", format: formatINR },
      { key: "postGoLive", label: "Post Go-live Support", format: formatINR },
      { key: "amc", label: "AMC (5Y)", format: formatINR },

      { key: "warrantyCost", label: "Warranty", format: formatINR },
      { key: "risk", label: "Risk", format: formatINR },

      { key: "consumables", label: "Consumables (5Y)", format: formatINR },



      { key: "focBenefits", label: "FOC (-)", format: formatINR },

      { key: "total", label: "Total Cost", format: formatINR },
    ];

    return fields.map((f) => {
      const row = { label: f.label };

      rows.forEach((v, idx) => {
        if (f.key === "lcCost") {
          row[`col_${idx}`] = v.hasLC ? f.format(v.lcCost) : "Not Applicable";
        } else {
          row[`col_${idx}`] = f.format ? f.format(v[f.key]) : (v[f.key] ?? "-");
        }
      });

      return row;
    });
  }, [rows]);

  // ------------------- UI -------------------

  return (
    <Paper
      id="tco_compare_view"
      elevation={isExporting ? 0 : 1}
      sx={{
        p: { xs: 1, sm: 2 },
        borderRadius: 2,
        width: "100%",
        overflow: "hidden",
        fontSize: isExporting ? `${14 * exportFontScale}px` : "14px",
        boxShadow: isExporting ? "none" : undefined,
      }}
    >
      <Backdrop
        sx={{ color: "#fff", zIndex: (themeValue) => themeValue.zIndex.drawer + 1 }}
        open={isDownloading && !isExporting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#333",
          }}
        >
          TCO Comparison Analysis
        </Box>
        {rows.length > 0 && (
          <DButton
            label={"EXPORT REPORT"}
            btnHeight="4rem"
            btnWidth={"18rem"}
            buyerBtn={true}
            onClick={handleDownloadPdf}
          />
        )}
      </Box>

      {/* <Box
        sx={{
          mt: 3,
          p: 2,
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
        }}
      > */}
      {/* <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            justifyContent: "space-between",
          }}
        > */}
      {/* Lowest */}
      {/* <Box>
            <Box sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>
              Lowest Avg TCO / Item
            </Box>
            <Box sx={{ fontSize: "18px", fontWeight: 700, color: "#2E7D32" }}>
              {ranked.length ? formatINR(lowest) : "N/A"}
            </Box>
            <Box sx={{ fontSize: "13px", color: "#666" }}>
              {ranked[0]?.vendor || "No vendor"}
            </Box> */}
      {/* </Box> */}

      {/* Highest */}
      {/* <Box>
            <Box sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>
              Highest Avg TCO / Item
            </Box>
            <Box sx={{ fontSize: "18px", fontWeight: 700, color: "#D32F2F" }}>
              {ranked.length ? formatINR(highest) : "N/A"}
            </Box>
            <Box sx={{ fontSize: "13px", color: "#666" }}>
              {ranked[ranked.length - 1]?.vendor || "No vendor"}
            </Box> */}
      {/* </Box> */}

      {/* Market Avg */}
      {/* <Box>
            <Box sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>
              Market Avg TCO / Item
            </Box>
            <Box sx={{ fontSize: "18px", fontWeight: 700, color: "#1976D2" }}>
              {ranked.length ? formatINR(average) : "N/A"}
            </Box>
            <Box sx={{ fontSize: "13px", color: "#666" }}>
              Across {ranked.length} vendors
            </Box> */}
      {/* </Box> */}

      {/* Savings */}
      {/* <Box>
            <Box sx={{ fontSize: "12px", color: "#666", mb: 0.5 }}>
              Cost Saving / Item
            </Box>
            <Box sx={{ fontSize: "18px", fontWeight: 700, color: "#4CAF50" }}>
              {ranked.length > 1 ? formatINR(savings) : "N/A"}
            </Box>
            <Box sx={{ fontSize: "13px", color: "#666" }}>Best vs Worst</Box>
          </Box> */}
      {/* </Box> */}

      {/* </Box> */}

      <Box
        sx={{
          width: "100%",
          mt: "8px",
          overflowX: "auto",
          overflowY: isExporting ? "visible" : "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { height: "6px" },
        }}
      >
        <TableContainer
          sx={{
            maxHeight: isExporting ? "none" : { xs: "60vh", sm: "70vh" },
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            overflow: isExporting ? "visible" : "auto",
            boxShadow: isExporting ? "none" : undefined,
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: rows.length * vendorColWidth + firstColWidth,
              tableLayout: "fixed",
              width: "100%",
            }}
          >
            {/* HEADER */}
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...headerStyle,
                    ...stickyLeft,
                    ...stickyHeader,
                    zIndex: 5,
                    width: firstColWidth,
                    minWidth: firstColWidth,
                    maxWidth: firstColWidth,
                    background: "#f1f5f9",
                    borderRight: "2px solid #b8caee",
                    fontSize: isExporting
                      ? `${15 * exportFontScale}px`
                      : headerStyle.fontSize,
                  }}
                >
                  Supplier
                </TableCell>

                {rows.map((v, i) => (
                  <TableCell
                    key={`head-${i}`}
                    align="center"
                    sx={{
                      ...headerStyle,
                      ...stickyHeader,
                      fontSize: isExporting
                        ? `${15 * exportFontScale}px`
                        : headerStyle.fontSize,
                    }}
                  >
                    {v.vendor}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {/* BODY */}
            <TableBody>
              {verticalRows.map((row, idx) => {
                const isTotalRow = row.label === "Total Cost";

                return (
                  <TableRow
                    key={idx}
                    hover
                    sx={
                      isTotalRow
                        ? { background: "#e3f2fd" } // row highlight color
                        : {}
                    }
                  >
                    <TableCell
                      sx={{
                        ...cellStyle,
                        ...stickyLeft,
                        background: isTotalRow ? "#bbdefb" : "#f8fafc",
                        fontFamily: "GilroyMedium",
                        width: firstColWidth,
                        minWidth: firstColWidth,
                        maxWidth: firstColWidth,
                        borderRight: "2px solid #b8caee",
                        zIndex: 4,
                        fontWeight: isTotalRow ? 700 : 500,
                        color: isTotalRow ? "#0d47a1" : "inherit",
                        fontSize: isTotalRow
                          ? `${16 * (isExporting ? exportFontScale : 1)}px`
                          : `${14 * (isExporting ? exportFontScale : 1)}px`,
                      }}
                    >
                      {row.label}
                    </TableCell>

                    {rows.map((v, i) => (
                      <TableCell
                        key={`cell-${idx}-${i}`}
                        align="center"
                        sx={{
                          ...cellStyle,
                          fontFamily: "GilroyMedium",
                          fontWeight: isTotalRow ? 700 : 500,
                          color: isTotalRow ? "#0d47a1" : "inherit",
                          background: isTotalRow ? "#e3f2fd" : "inherit",
                          fontSize: isTotalRow
                            ? `${16 * (isExporting ? exportFontScale : 1)}px`
                            : `${14 * (isExporting ? exportFontScale : 1)}px`,
                        }}
                      >
                        {row[`col_${i}`]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
};

export default TCOComparison;
